import React from 'react';
import { MODULE_PERMISSIONS } from '../utils/modulePermissions';
import './ModuleAccessGrid.css';

export interface AccessModule {
  id: string;
  name: string;
}

type AccessMap = Record<string, any>;

interface ModuleAccessGridProps {
  modules: AccessModule[];
  access: AccessMap;
  onChange: (next: AccessMap) => void;
  renderExtra?: (moduleId: string) => React.ReactNode;
}

const readValue = (access: AccessMap, moduleId: string) => {
  const id = moduleId.toLowerCase();
  return access[moduleId] ?? access[id];
};

const allPermissions = (moduleId: string, previous?: any) => {
  const id = moduleId.toLowerCase();
  const defs = MODULE_PERMISSIONS[id] || [];
  if (defs.length === 0) return true;
  const next: Record<string, any> = {};
  defs.forEach((perm) => { next[perm.key] = true; });
  if (id === 'extraits') {
    next.full = true;
    next.view = true;
    next.select = true;
    next.propose = true;
    next.suivi = true;
    next.shows = [];
  }
  if (id === 'rh' || id === 'planning') {
    next.view = true;
  }
  if (id === 'achats' && previous && Array.isArray(previous.lists)) {
    next.lists = previous.lists;
  }
  if (id === 'captations' && previous && Array.isArray(previous.sites)) {
    next.sites = previous.sites;
  }
  if (id === 'vault' && previous && Array.isArray(previous.groups)) {
    next.groups = previous.groups;
  }
  return next;
};

const defaultPermissions = (moduleId: string) => {
  const id = moduleId.toLowerCase();
  const defs = MODULE_PERMISSIONS[id] || [];
  if (defs.length === 0) return true;
  const next: Record<string, boolean> = {};
  if (id === 'filemanager') {
    defs.forEach((perm) => { next[perm.key] = perm.key === 'shows'; });
  } else if (id === 'lexique') {
    defs.forEach((perm) => { next[perm.key] = perm.key === 'conventions' || perm.key === 'lexique'; });
  } else if (id === 'extraits') {
    next.full = true;
    next.view = true;
    next.select = true;
    next.propose = true;
    next.suivi = false;
  } else if (id === 'rh' || id === 'planning') {
    next.view = true;
    next.manager = false;
  } else {
    defs.forEach((perm) => { next[perm.key] = perm.key === 'view' || perm.key === 'access'; });
  }
  return next;
};

const withoutModule = (access: AccessMap, moduleId: string): AccessMap => {
  const id = moduleId.toLowerCase();
  const next = { ...access };
  delete next[moduleId];
  delete next[id];
  return next;
};

const ModuleAccessGrid: React.FC<ModuleAccessGridProps> = ({ modules, access, onChange, renderExtra }) => {
  const orderedModules = [...modules].sort((a, b) =>
    a.name.localeCompare(b.name, 'fr', { sensitivity: 'base' })
  );

  const selectAll = () => {
    const next: AccessMap = {};
    modules.forEach((module) => {
      const id = module.id.toLowerCase();
      next[id] = allPermissions(module.id, readValue(access, module.id));
    });
    onChange(next);
  };

  const clearAll = () => onChange({});

  const selectModule = (moduleId: string) => {
    const id = moduleId.toLowerCase();
    onChange({
      ...withoutModule(access, moduleId),
      [id]: allPermissions(moduleId, readValue(access, moduleId))
    });
  };

  const toggleModule = (moduleId: string, enabled: boolean) => {
    const id = moduleId.toLowerCase();
    if (!enabled) {
      onChange(withoutModule(access, moduleId));
      return;
    }
    const previous = readValue(access, moduleId);
    const defaults = defaultPermissions(moduleId);
    if (
      id === 'achats'
      && previous
      && typeof previous === 'object'
      && Array.isArray(previous.lists)
      && defaults
      && typeof defaults === 'object'
    ) {
      (defaults as Record<string, unknown>).lists = previous.lists;
    }
    if (
      id === 'captations'
      && previous
      && typeof previous === 'object'
      && Array.isArray(previous.sites)
      && defaults
      && typeof defaults === 'object'
    ) {
      (defaults as Record<string, unknown>).sites = previous.sites;
    }
    if (
      id === 'vault'
      && previous
      && typeof previous === 'object'
      && Array.isArray(previous.groups)
      && defaults
      && typeof defaults === 'object'
    ) {
      (defaults as Record<string, unknown>).groups = previous.groups;
    }
    onChange({
      ...withoutModule(access, moduleId),
      [id]: defaults
    });
  };

  const togglePerm = (moduleId: string, key: string, enabled: boolean) => {
    const id = moduleId.toLowerCase();
    const current = readValue(access, moduleId);
    const existing = current && typeof current === 'object' ? current : {};
    const nextPerms = id === 'extraits' && key === 'full'
      ? {
          ...existing,
          full: enabled,
          view: enabled,
          select: enabled,
          propose: enabled,
          ...(enabled ? { shows: [] } : {}),
        }
      : { ...existing, [key]: enabled };
    onChange({
      ...withoutModule(access, moduleId),
      [id]: nextPerms
    });
  };

  return (
    <div className="mag">
      <div className="mag-bar">
        <button type="button" className="mag-action" onClick={selectAll}>Tout sélectionner</button>
        <button type="button" className="mag-action mag-action-quiet" onClick={clearAll}>Tout retirer</button>
      </div>
      <div className="mag-list">
        {orderedModules.map((module) => {
          const id = module.id.toLowerCase();
          const raw = readValue(access, module.id);
          const enabled = !!raw;
          const current = raw && typeof raw === 'object' ? raw : {};
          const defs = MODULE_PERMISSIONS[id] || [];
          const groups = defs.reduce<Record<string, typeof defs>>((acc, perm) => {
            const group = perm.group || '';
            if (!acc[group]) acc[group] = [];
            acc[group].push(perm);
            return acc;
          }, {});
          return (
            <section key={module.id} className={`mag-app${enabled ? ' is-on' : ''}`}>
              <div className="mag-head">
                <button
                  type="button"
                  className={`mag-switch${enabled ? ' is-on' : ''}`}
                  role="switch"
                  aria-checked={enabled}
                  aria-label={module.name}
                  onClick={() => toggleModule(module.id, !enabled)}
                >
                  <span />
                </button>
                <strong className="mag-name">{module.name}</strong>
                <button type="button" className="mag-action" onClick={() => selectModule(module.id)}>Tout</button>
              </div>
              {enabled && defs.length > 0 && (
                <div className="mag-groups">
                  {Object.entries(groups).map(([groupName, perms]) => (
                    <div key={groupName || 'default'} className="mag-group">
                      {groupName && <div className="mag-group-label">{groupName}</div>}
                      <div className="mag-chips">
                        {perms.map((perm) => {
                          const showLimit = id === 'extraits'
                            && Array.isArray(current.shows)
                            && current.shows.length > 0;
                          const checked = id === 'extraits' && perm.key === 'full'
                            ? !!current.full && !showLimit
                            : !!current[perm.key];
                          return (
                            <button
                              key={perm.key}
                              type="button"
                              className={`mag-chip${checked ? ' is-on' : ''}`}
                              aria-pressed={checked}
                              onClick={() => togglePerm(id, perm.key, !checked)}
                            >
                              {perm.label}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  ))}
                </div>
              )}
              {enabled && renderExtra?.(id)}
            </section>
          );
        })}
      </div>
    </div>
  );
};

export default ModuleAccessGrid;
